import React, { createContext, useContext, useEffect, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { sumFormFormatter } from '@shared/helpers/sumFormFormatter';
import { CustomModal, UiButton } from '@shared/ui';
import { AddPartnerModal } from '@widgets/AddPayments/ui/AddPartnerModal/AddPartnerModal';

import { EditPaymentBody } from './EditPaymentBody/EditPaymentBody';
import cls from './EditPaymentModal.module.css';
import { fetchPaymentsFilters } from '../../../../store/slices/paymentSlice/action';
import { paymentActions } from '../../../../store/slices/paymentSlice/paymentSlice';
import { fetchEditPayment, fetchRemovePayment } from '../../lib/model/PaymentListApi';
import { Context, updateFilter } from '../PaymentsList';

export const ModalContext = createContext({});

export const EditPaymentModal = ({ isOpen, onShowModal, payment }) => {
    const { t } = useTranslation();
    const allPartners = useSelector((state) => state.paymentReducer.partnersSelectList);
    const { pagination, filter } = useSelector((state) => state.paymentReducer);

    const { accountsSelectList, archiveAccountList } = useSelector((state) => state.paymentReducer);
    const defaultAccount = accountsSelectList.find((account) => account.account.default_account);

    // todo move in shared/lib getDateWithoutUtc
    const paymentDateWithoutUtc = moment(payment.payment_date.split('T')[0]).format('DD.MM.YYYY');
    const accrualDateWithoutUtc = moment(payment.accrual_date.split('T')[0]).format('DD.MM.YYYY');

    let firstStepAccountInit = defaultAccount.value;

    if (payment && payment.correspondent) {
        firstStepAccountInit =
            payment.correspondent.title !== undefined
                ? payment.correspondent.guid
                : defaultAccount.value;

        if (firstStepAccountInit) {
            const isArchive = archiveAccountList.some(
                (account) => account.value === firstStepAccountInit
            );
            firstStepAccountInit = isArchive ? defaultAccount.value : payment.correspondent.guid;
        }
    }

    const dispatch = useDispatch();
    const { setCurrentPayment, currentPayment } = useContext(Context);
    const [isOpenPartner, setIsOpenPartner] = useState(false);

    let partnersInit = '';
    let fullNameInit = '';

    if (payment.correspondent) {
        partnersInit = payment.correspondent.guid ? payment.correspondent.guid : '';

        const currentPartner = allPartners.find(
            (partner) => partner.value === payment.correspondent.guid
        );
        fullNameInit = currentPartner ? currentPartner.fullName : '';
    }

    if (payment.payload.name !== undefined) {
        fullNameInit = payment.payload.name;
    }

    let estimateInit = null;

    if (payment.sum_divisions.length) {
        if (payment.sum_divisions[0].project.length === 0) {
            estimateInit = null;
        } else if (payment.sum_divisions[0].estimate.id !== undefined) {
            estimateInit = payment.sum_divisions[0].estimate.guid;
        } else {
            estimateInit = payment.sum_divisions[0].project.id;
        }
    }

    let distributeEstimatesInit = [];

    if (payment.sum_divisions.length > 1) {
        const dis = payment.sum_divisions.map((estimate, i) => {
            let estimateSelect;

            if (estimate.project.length === 0) {
                estimateSelect = null;
            } else if (estimate.estimate.id !== undefined) {
                estimateSelect = estimate.estimate.guid;
            } else {
                estimateSelect = estimate.project.id;
            }

            const id = Date.now() + i;
            const { sum } = estimate;
            const percent = (Number(sum) / Number(payment.sum)) * 100;

            return {
                estimateSelect,
                id,
                sum,
                percent,
            };
        });

        distributeEstimatesInit = dis;
    }

    const [formState, setFormState] = useState({
        account: payment.account.guid,
        partners: partnersInit,
        fullName: fullNameInit,
        estimates: estimateInit,
        expenditures: payment.payment_item ? payment.payment_item.guid : null,
        firstStepAccount: firstStepAccountInit,
        paymentDate: paymentDateWithoutUtc,
        accrualDate: accrualDateWithoutUtc,
        sum: sumFormFormatter(String(payment.sum)),
        receivedSum: payment.received_sum ? sumFormFormatter(String(payment.received_sum)) : 0,
        purposePayment: payment.target,
        purposeName: '',
        comment: payment.comment,
        invoicePayment: [],
        oldFiles: payment.attachments,
        distributeEstimates: distributeEstimatesInit,
    });

    const [isBetweenAccount, setIsBetweenAccount] = useState(payment.type === 'transfer');
    const [errors, setErrors] = useState({
        account: null,
        sum: null,
        paymentItem: null,
        estimate: null,
        correspondent: null,
        receiverAccount: null,
        purposePayment: null,
    });

    let selectedAccount;

    if (currentPayment.account.is_archive) {
        selectedAccount = [...accountsSelectList, ...archiveAccountList].find(
            (account) => account.value === formState.account
        );
    } else {
        selectedAccount = accountsSelectList.find((account) => account.value === formState.account);
    }

    const selectedSecondAccount = accountsSelectList.find(
        (account) => account.value === formState.firstStepAccount
    );

    const onRemoveButton = () => {
        loader.show();
        const callback = async (data) => {
            await updateFilter(
                dispatch,
                paymentActions,
                fetchPaymentsFilters,
                filter,
                pagination.current
            );
            await onShowModal();
            await loader.hide();
        };
        fetchRemovePayment(currentPayment.guid, callback);
    };
    const onSubmitButton = () => {
        loader.show();

        const errorHandler = (data) => {
            const initialState = {
                account: null,
                sum: null,
                paymentItem: null,
                estimate: null,
                correspondent: null,
                receiverAccount: null,
                purposePayment: null,
            };

            data.errors.forEach((error) => {
                initialState[error.name] = error.messages.toString();
            });

            setErrors(initialState);
        };

        const callback = async () => {
            onShowModal();
            setCurrentPayment(null);

            loader.hide();

            await updateFilter(
                dispatch,
                paymentActions,
                fetchPaymentsFilters,
                filter,
                pagination.current
            );
        };

        const formData = new FormData();

        formData.append('paymentDate', formState.paymentDate);
        formData.append('accrualDate', formState.accrualDate);
        formData.append('account', formState.account);
        formData.append('target', formState.purposePayment);
        formData.append('sum', formState.sum.replace(/,/g, '.'));

        if (isBetweenAccount === false) {
            formData.append('correspondent', formState.partners ? formState.partners : 0);
            formData.append('paymentItem', formState.expenditures ? formState.expenditures : 0);

            if (formState.distributeEstimates.length === 0) {
                formData.append(
                    'divisions[0][estimateOrProject]',
                    formState.estimates ? formState.estimates : ''
                );
                formData.append('divisions[0][percent]', 100);
                formData.append('divisions[0][sum]', formState.sum.replace(/,/g, '.'));
            } else {
                formState.distributeEstimates.forEach((estimate, index) => {
                    formData.append(
                        `divisions[${index}][estimateOrProject]`,
                        estimate.estimateSelect
                    );
                    formData.append(`divisions[${index}][percent]`, estimate.percent);
                    formData.append(`divisions[${index}][sum]`, estimate.sum);
                });
            }
        } else {
            formData.append('receiverAccount', formState.firstStepAccount);
            const receiveSum =
                selectedAccount.account.currency.sign ===
                selectedSecondAccount.account.currency.sign
                    ? formState.sum.replace(/,/g, '.')
                    : formState.receivedSum.replace(/,/g, '.');

            formData.append('receiveSum', receiveSum);
        }

        formData.append('comment', formState.comment);
        formState.invoicePayment.forEach((file) => {
            formData.append('attachments[]', file);
        });
        formData.append('old_files', formState.oldFiles.map((file) => file.id).join(','));

        fetchEditPayment(currentPayment.guid, formData, callback, errorHandler);
    };

    const modalContextValue = {
        formState,
        setFormState,
        errors,
        setErrors,
        isBetweenAccount,
        setIsBetweenAccount,
        selectedSecondAccount,
        selectedAccount,
    };

    return (
        <ModalContext.Provider value={modalContextValue}>
            <CustomModal contentClasses={cls.modal} isOpen={isOpen} onHideModal={onShowModal}>
                <CustomModal.Header>
                    {t('EditPaymentModal.return.ModalHeader')} №{payment.number}
                </CustomModal.Header>
                <CustomModal.Body>
                    <EditPaymentBody setIsOpenPartner={setIsOpenPartner} />
                </CustomModal.Body>
                <CustomModal.Footer>
                    <div className={classNames(cls.footer)}>
                        {!payment.account.integration &&
                        !payment.account.is_archive &&
                        !payment.is_locked_for_editing ? (
                            <UiButton
                                onClick={onRemoveButton}
                                btnText={t('button.delete.text')}
                                btnColor="danger"
                            />
                        ) : (
                            <div />
                        )}
                        <div className={classNames(cls.buttonBlock)}>
                            <UiButton
                                onClick={onShowModal}
                                btnText={
                                    payment.account.is_archive
                                        ? t('button.close.text')
                                        : t('button.cancel.text')
                                }
                                btnColor="secondary"
                            />
                            {!payment.account.is_archive && !payment.is_locked_for_editing && (
                                <UiButton
                                    onClick={onSubmitButton}
                                    btnText={t('button.save.text')}
                                />
                            )}
                        </div>
                    </div>
                </CustomModal.Footer>
            </CustomModal>
            {isOpenPartner && (
                <AddPartnerModal
                    setMainForm={setFormState}
                    onShowModal={() => setIsOpenPartner(false)}
                    isOpen={isOpenPartner}
                />
            )}
        </ModalContext.Provider>
    );
};
